import React, { useState, useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { FiX, FiSearch, FiRotateCcw, FiPrinter, FiEye, FiCalendar, FiSlash, FiTrash2 } from 'react-icons/fi';
import { colors, borderRadius, typography, shadows } from '../../theme/colors';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import useCurrency from '../../hooks/useCurrency';
import { localDateStr } from '../../utils/localDate';

const POSHistoryModal = ({
  isOpen,
  onClose,
  companyId,
  activeBranchId,
  activeCounterId,
  onReprint,
  onCreateReturn,
  onVoid,
  onDelete
}) => {
  const { formatCurrency } = useCurrency();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState(localDateStr());
  const [selectedTxn, setSelectedTxn] = useState(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (isOpen && companyId) {
      loadTransactions();
      setTimeout(() => searchRef.current?.focus(), 150);
    }
    if (!isOpen) {
      setSelectedTxn(null);
      setSearchQuery('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, companyId, dateFilter, activeBranchId, activeCounterId]);

  const loadTransactions = async () => {
    setLoading(true);
    try {
      const txnRef = collection(db, 'companies', companyId, 'transactions');
      const q = query(txnRef, where('posTransaction', '==', true));
      const snap = await getDocs(q);
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(t => {
          // Scope to the active counter — each counter sees only its own sales.
          if (activeCounterId && t.counterId !== activeCounterId) {
            return false;
          }
          // Filter by branch if active (skip for 'all')
          if (activeBranchId && activeBranchId !== 'all' && t.branchId !== activeBranchId) {
            return false;
          }
          if (dateFilter && t.docDate) {
            return t.docDate === dateFilter;
          }
          return true;
        })
        .sort((a, b) => {
          const da = a.createdAt || a.docDate || '';
          const db2 = b.createdAt || b.docDate || '';
          return db2.localeCompare(da);
        });
      setTransactions(list);
    } catch (err) {
      console.error('Error loading POS history:', err);
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return transactions;
    const q = searchQuery.toLowerCase();
    return transactions.filter(t =>
      (t.docNumber || '').toLowerCase().includes(q) ||
      (t.partyName || '').toLowerCase().includes(q) ||
      String(t.totalAmount || 0).includes(q)
    );
  }, [transactions, searchQuery]);

  const getPaymentLabel = (txn) => {
    if (txn.paymentMethod === 'split') return 'Split';
    if (txn.paymentMethod === 'credit') return 'Credit';
    if (txn.paymentMethod === 'card') return 'Card';
    return 'Cash';
  };

  const formatTime = (isoStr) => {
    if (!isoStr) return '';
    try {
      const d = new Date(isoStr);
      return d.toLocaleTimeString('en-PK', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
  };

  if (!isOpen) return null;

  return (
    <Overlay onClick={onClose}>
      <ModalContainer onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>POS Transactions</ModalTitle>
          <CloseBtn onClick={onClose}><FiX size={20} /></CloseBtn>
        </ModalHeader>

        <FilterRow>
          <SearchBox>
            <FiSearch size={16} />
            <SearchInput
              ref={searchRef}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by receipt#, customer..."
            />
            {searchQuery && (
              <ClearBtn onClick={() => setSearchQuery('')}><FiX size={14} /></ClearBtn>
            )}
          </SearchBox>
          <DateBox>
            <FiCalendar size={14} />
            <DateInput
              type="date"
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
            />
          </DateBox>
        </FilterRow>

        <ListContainer>
          {loading ? (
            <EmptyMsg>Loading...</EmptyMsg>
          ) : filtered.length === 0 ? (
            <EmptyMsg>No transactions found</EmptyMsg>
          ) : (
            filtered.map(txn => {
              const isReturn = txn.tranTypeCode === 'SR';
              const isCancelled = txn.status === 'Cancelled';
              const isSelected = selectedTxn?.id === txn.id;
              return (
                <TxnRow
                  key={txn.id}
                  $selected={isSelected}
                  $cancelled={isCancelled}
                  onClick={() => setSelectedTxn(isSelected ? null : txn)}
                >
                  <TxnMain>
                    <TxnDocNum>{txn.docNumber}</TxnDocNum>
                    <TxnTypeBadge $isReturn={isReturn}>
                      {isReturn ? 'Return' : 'Sale'}
                    </TxnTypeBadge>
                    {isCancelled && <CancelledBadge>VOIDED</CancelledBadge>}
                    <TxnPayment>{getPaymentLabel(txn)}</TxnPayment>
                    <TxnAmount $isReturn={isReturn} $cancelled={isCancelled}>
                      {isReturn ? '-' : ''}{formatCurrency(txn.totalAmount || 0)}
                    </TxnAmount>
                  </TxnMain>
                  <TxnSub>
                    <span>{txn.partyName || 'Walk-in Customer'}</span>
                    <span>{formatTime(txn.createdAt)}</span>
                    <span>{txn.lines?.length || 0} items</span>
                  </TxnSub>

                  {isSelected && (
                    <TxnDetail>
                      <DetailSection>
                        <DetailLabel>Items</DetailLabel>
                        {(txn.lines || []).map((line, i) => (
                          <DetailLine key={i}>
                            <span>{line.itemName}</span>
                            <span>{line.quantity} x {formatCurrency(line.unitPrice)} = {formatCurrency(line.lineTotal || (line.quantity * line.unitPrice))}</span>
                          </DetailLine>
                        ))}
                      </DetailSection>
                      {txn.posPayments && txn.posPayments.length > 0 && (
                        <DetailSection>
                          <DetailLabel>Payments</DetailLabel>
                          {txn.posPayments.filter(p => p.amount > 0).map((p, i) => (
                            <DetailLine key={i}>
                              <span>{(p.method || '').toUpperCase()} {p.accountName ? `(${p.accountName})` : ''}</span>
                              <span>{formatCurrency(p.amount)}</span>
                            </DetailLine>
                          ))}
                        </DetailSection>
                      )}
                      <TxnActions>
                        {onReprint && !isReturn && (
                          <ActionBtn onClick={(e) => { e.stopPropagation(); onReprint(txn); }}>
                            <FiPrinter size={14} /> Reprint
                          </ActionBtn>
                        )}
                        {onCreateReturn && !isReturn && !isCancelled && (
                          <ActionBtn $danger onClick={(e) => { e.stopPropagation(); onCreateReturn(txn); }}>
                            <FiRotateCcw size={14} /> Return
                          </ActionBtn>
                        )}
                        {onVoid && !isReturn && !isCancelled && (
                          <ActionBtn $danger onClick={(e) => { e.stopPropagation(); onVoid(txn); }}>
                            <FiSlash size={14} /> Void
                          </ActionBtn>
                        )}
                        {onDelete && (
                          <ActionBtn $delete onClick={(e) => { e.stopPropagation(); onDelete(txn); }}>
                            <FiTrash2 size={14} /> Delete
                          </ActionBtn>
                        )}
                        {isCancelled && (
                          <ActionNote>Voided &middot; stock restored</ActionNote>
                        )}
                        {isReturn && txn.parentId && (
                          <ActionNote>Return of receipt</ActionNote>
                        )}
                      </TxnActions>
                    </TxnDetail>
                  )}
                </TxnRow>
              );
            })
          )}
        </ListContainer>

        <ModalFooter>
          <FooterInfo>{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</FooterInfo>
          <FooterCloseBtn onClick={onClose}>Close</FooterCloseBtn>
        </ModalFooter>
      </ModalContainer>
    </Overlay>
  );
};

// ── Styled Components ──

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: ${borderRadius.lg};
  width: 640px;
  max-width: 95vw;
  max-height: 85vh;
  display: flex;
  flex-direction: column;
  box-shadow: ${shadows.xl};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid ${colors.border.light};
`;

const ModalTitle = styled.h3`
  font-size: ${typography.fontSize.lg};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.primary};
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: ${colors.text.secondary};
  cursor: pointer;
  padding: 4px;
  &:hover { color: ${colors.error.main}; }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 8px;
  padding: 12px 20px;
  border-bottom: 1px solid ${colors.border.light};
`;

const SearchBox = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};
  &:focus-within { border-color: ${colors.primary.main}; }
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: ${typography.fontSize.sm};
  &::placeholder { color: ${colors.text.tertiary}; }
`;

const ClearBtn = styled.button`
  background: none;
  border: none;
  color: ${colors.text.tertiary};
  cursor: pointer;
  padding: 2px;
  &:hover { color: ${colors.text.primary}; }
`;

const DateBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
  color: ${colors.text.secondary};
`;

const DateInput = styled.input`
  border: none;
  outline: none;
  font-size: ${typography.fontSize.sm};
  color: ${colors.text.primary};
`;

const ListContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 200px;
  &::-webkit-scrollbar { width: 4px; }
  &::-webkit-scrollbar-thumb { background: ${colors.neutral[300]}; border-radius: 4px; }
`;

const EmptyMsg = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: ${colors.text.tertiary};
  font-size: ${typography.fontSize.sm};
`;

const TxnRow = styled.div`
  padding: 10px 20px;
  border-bottom: 1px solid ${colors.neutral[100]};
  cursor: pointer;
  background: ${({ $selected, $cancelled }) =>
    $cancelled ? '#FFF5F5' : $selected ? colors.neutral[50] : 'transparent'};
  opacity: ${({ $cancelled }) => ($cancelled ? 0.7 : 1)};
  &:hover { background: ${({ $cancelled }) => ($cancelled ? '#FFEEEE' : colors.neutral[50])}; }
`;

const CancelledBadge = styled.span`
  font-size: 9px;
  font-weight: ${typography.fontWeight.bold};
  letter-spacing: 0.5px;
  padding: 2px 8px;
  border-radius: 10px;
  background: ${colors.error.main};
  color: #fff;
`;

const TxnMain = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TxnDocNum = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: ${colors.text.primary};
  min-width: 130px;
`;

const TxnTypeBadge = styled.span`
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  padding: 2px 8px;
  border-radius: 10px;
  background: ${({ $isReturn }) => $isReturn ? '#FFEBEE' : '#E8F5E9'};
  color: ${({ $isReturn }) => $isReturn ? colors.error.main : colors.success.main};
`;

const TxnPayment = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.secondary};
  flex: 1;
`;

const TxnAmount = styled.span`
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.bold};
  color: ${({ $isReturn, $cancelled }) =>
    $cancelled ? colors.text.tertiary : ($isReturn ? colors.error.main : colors.text.primary)};
  text-decoration: ${({ $cancelled }) => ($cancelled ? 'line-through' : 'none')};
`;

const TxnSub = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 4px;
  font-size: 11px;
  color: ${colors.text.tertiary};
`;

const TxnDetail = styled.div`
  margin-top: 10px;
  padding: 10px;
  background: white;
  border: 1px solid ${colors.border.light};
  border-radius: ${borderRadius.sm};
`;

const DetailSection = styled.div`
  margin-bottom: 8px;
  &:last-child { margin-bottom: 0; }
`;

const DetailLabel = styled.div`
  font-size: 10px;
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.text.secondary};
  text-transform: uppercase;
  margin-bottom: 4px;
`;

const DetailLine = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.primary};
  padding: 2px 0;
`;

const TxnActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid ${colors.border.light};
`;

const ActionBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid ${({ $danger, $delete }) => ($danger || $delete) ? colors.error.main : colors.primary.main};
  border-radius: ${borderRadius.sm};
  background: ${({ $delete }) => $delete ? colors.error.main : 'white'};
  color: ${({ $danger, $delete }) => $delete ? 'white' : ($danger ? colors.error.main : colors.primary.main)};
  font-size: ${typography.fontSize.xs};
  font-weight: ${typography.fontWeight.semibold};
  cursor: pointer;
  &:hover {
    background: ${({ $danger, $delete }) => ($danger || $delete) ? colors.error.main : colors.primary.main};
    color: white;
    opacity: ${({ $delete }) => $delete ? 0.9 : 1};
  }
`;

const ActionNote = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.tertiary};
  font-style: italic;
`;

const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  border-top: 1px solid ${colors.border.light};
`;

const FooterInfo = styled.span`
  font-size: ${typography.fontSize.xs};
  color: ${colors.text.tertiary};
`;

const FooterCloseBtn = styled.button`
  padding: 8px 20px;
  border: 1px solid ${colors.border.main};
  border-radius: ${borderRadius.sm};
  background: white;
  font-size: ${typography.fontSize.sm};
  cursor: pointer;
  &:hover { background: ${colors.neutral[100]}; }
`;

export default POSHistoryModal;
